/**
 * <s-product-carousel> — PDP media carousel.
 *
 * The track is a native scroll-snap list, so swipe, trackpad and keyboard scrolling
 * work without JavaScript. This element adds previous/next buttons, a "n / total"
 * counter and an optional autoplay that advances one slide every `data-interval` ms.
 * Autoplay never runs under prefers-reduced-motion, pauses while the pointer or focus
 * is inside, stops for good once the shopper navigates manually, and exposes a
 * pause/play toggle (WCAG 2.2.2).
 *
 * Required markup:
 *   [data-carousel-track]  — horizontally scrolling element; its children are the slides.
 *   [data-carousel-prev]   — previous button.
 *   [data-carousel-next]   — next button.
 *   [data-carousel-current]— element whose text becomes the 1-based slide index.
 *   [data-carousel-toggle] — optional pause/play button with data-label-pause / data-label-play;
 *                            it holds [data-icon-pause] and [data-icon-play] children.
 */
class SProductCarousel extends HTMLElement {
  #ac;
  #timer = 0;
  #index = 0;
  #playing = false;
  #hovered = false;
  #focused = false;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.track;
    if (!track || this.slides.length < 2) return;

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.#manual(-1), opts);
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.#manual(1), opts);
    this.querySelector('[data-carousel-toggle]')?.addEventListener('click', () => this.#setPlaying(!this.#playing, true), opts);
    track.addEventListener('pointerdown', () => this.#setPlaying(false, true), opts);
    track.addEventListener('wheel', () => this.#setPlaying(false, true), { ...opts, passive: true });
    this.addEventListener('pointerenter', () => { this.#hovered = true; this.#schedule(); }, opts);
    this.addEventListener('pointerleave', () => { this.#hovered = false; this.#schedule(); }, opts);
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, opts);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node|null} */ (event.relatedTarget))) {
        this.#focused = false;
        this.#schedule();
      }
    }, opts);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#index = this.slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          this.#render();
        }
      }
    }, { root: track, threshold: [0.6] });
    for (const slide of this.slides) this.#observer.observe(slide);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#setPlaying(this.hasAttribute('data-autoplay') && !reduceMotion, false);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    window.clearTimeout(this.#timer);
  }

  get track() {
    return /** @type {HTMLElement|null} */ (this.querySelector('[data-carousel-track]'));
  }

  get slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.track?.children ?? []));
  }

  #manual(step) {
    this.#setPlaying(false, true);
    this.#go(this.#index + step);
  }

  #go(index) {
    const slides = this.slides;
    const track = this.track;
    if (!track || slides.length === 0) return;
    const next = (index + slides.length) % slides.length;
    const slide = slides[next];
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const offset = isRtl
      ? slide.offsetLeft + slide.offsetWidth - track.clientWidth
      : slide.offsetLeft;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: offset, behavior: smooth ? 'smooth' : 'auto' });
    this.#index = next;
    this.#render();
  }

  #setPlaying(playing, userInitiated) {
    if (userInitiated && !playing) this.removeAttribute('data-autoplay');
    this.#playing = playing;
    const toggle = this.querySelector('[data-carousel-toggle]');
    if (toggle) {
      const label = playing ? toggle.getAttribute('data-label-pause') : toggle.getAttribute('data-label-play');
      if (label) toggle.setAttribute('aria-label', label);
      toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
      toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
    }
    this.#schedule();
  }

  #schedule() {
    window.clearTimeout(this.#timer);
    if (!this.#playing || this.#hovered || this.#focused) return;
    const interval = Number(this.getAttribute('data-interval')) || 4000;
    this.#timer = window.setTimeout(() => {
      this.#go(this.#index + 1);
      this.#schedule();
    }, interval);
  }

  #render() {
    const current = this.querySelector('[data-carousel-current]');
    if (current) current.textContent = String(this.#index + 1);
  }
}

if (!customElements.get('s-product-carousel')) {
  customElements.define('s-product-carousel', SProductCarousel);
}
